/**
 * AccessApprovalCard — approve/decline UI for a MUTATION on the agent
 * access / sharing surface (grant access, team membership, share-link
 * create/send/revoke, permission revoke).
 *
 * The worker's mutation tool pends a question whose payload rides the
 * SAME wire the credential + A2UI prompts proved out: header
 * 'AccessRequest' with the `{ tool, action, payload, summary }` JSON in
 * `options[0].description` (parseAccessRequest in registerAccessTools.ts).
 *
 * On Approve the BROWSER performs the REST call (the user's session — the
 * worker deliberately does NOT hold the grant authority) and threads the
 * API outcome back through the question reply as the `_result`
 * side-channel (same mechanism as cred_to_env's `_entryId`):
 *
 *   onReply([['Approved']], <api result | { error }>)
 *
 * On Decline: onReply([['Decline']]) with no result.
 *
 * Theming: rendered inline in the message list (inside a ThemedArea), so
 * it uses `themed-*` utility classes directly — same as PermissionPrompt /
 * CredentialPrompt. (No manual getThemeClass; that's only for portals /
 * modals rendered OUTSIDE a ThemedArea.)
 */
import { useState } from 'react'
import {
  ShieldCheck,
  UsersRound,
  Share2,
  FolderSymlink,
  Check,
  X,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Megaphone,
} from 'lucide-react'
import {
  performAccessAction,
  getAccessActionSpec,
  permissionLabel,
  targetTypeLabel,
  type AccessRequestMeta,
} from './accessToolActions.js'
import type { QuestionRequest } from './hooks.js'

interface Props {
  request: QuestionRequest
  meta: AccessRequestMeta
  /** Reply through the question pipe. `result` is threaded as the
   *  `_result` side-channel so the worker can relay the API outcome. */
  onReply: (answers: string[][], result?: unknown) => void
}

/** Human title per tool.action (fallback: humanized action). */
const TITLES: Record<string, string> = {
  'manage_access.grant': 'Grant access',
  'manage_access.revoke': 'Revoke access',
  'team.create': 'Create team',
  'team.add_member': 'Add team member',
  'team.remove_member': 'Remove team member',
  'team.set_team_admin': 'Change team role',
  'share_link.create': 'Create share link',
  'share_link.send_code': 'Send access code',
  'share_link.revoke': 'Revoke share link',
  'chain_folder.create_mount': 'Create a .chain mount',
}

function iconFor(tool: string): React.ReactNode {
  const cls = 'text-yellow-400'
  switch (tool) {
    case 'team':
      return <UsersRound size={14} className={cls} />
    case 'share_link':
      return <Share2 size={14} className={cls} />
    case 'chain_folder':
      return <FolderSymlink size={14} className={cls} />
    default:
      return <ShieldCheck size={14} className={cls} />
  }
}

/** Humanize a payload key ("folderName" → "Folder", "targetId" → skip). */
const KEY_LABELS: Record<string, string> = {
  folderName: 'Folder',
  folderPath: 'Folder',
  teamName: 'Team',
  name: 'Name',
  email: 'Person',
  targetName: 'Target',
  targetLabel: 'Target',
  fileName: 'File',
  role: 'Role',
  permission: 'Access',
  level: 'Access',
  kind: 'Kind',
}

/** Keys we never surface (ids/opaque handles — the name variants cover
 *  them for the human). */
// THE RULE: a card names its target in its SUMMARY, resolved server-side; ids
// stay hidden. `manage_access` follows it on BOTH axes — the folder AND the
// grantee (a pdapp or team resolves to its name; a person is already an email
// and a role already a role name). Its grant and revoke summaries echo
// the CALLER'S OWN path when they supplied one, and otherwise carry the folder's
// LEAF NAME, looked up from the id. Never the full path: the tree lookups are
// tenant-scoped and carry NO permission check, so a path would print an entire
// ancestor chain for a folder the actor may not be able to read. And never a
// model-supplied label, which could name a different folder than the id acts on.
//
// `share_link.create` follows it too — it names its target (caller's own path,
// else the leaf name) and spells out WHO gets the link, because "*"
// (anyone-with-link) counted as an ordinary recipient in the old wording and was
// invisible. It never advertises an `edit` an app link cannot carry, because the
// tool REFUSES that combination outright rather than printing a downgraded
// level — a card that printed the clamped level disagreed with the wire, which
// still carried edit, and the server answered 403 about Edit.
//
// `chain_folder.create_mount` renders here TOO, since #518A — note the ACTION.
// `chain_folder.create` does NOT: that name is the old-worker `ctx.clientTool`
// dispatch, which raises no card and is retained in ACCESS_ACTIONS only so a
// previous-build worker keeps working. The card deliberately carries a name no
// client table resolves, because an OLD bundle decides "the browser performs
// this" by FINDING an entry and can never be taught otherwise.
//
// Before #518A a `.chain` mount had no consent surface at all — the user saw
// the model's raw tool-call args and nothing else, and the summary naming the
// mount rode a contract nothing displayed. That summary is now what a human
// reads before the mount is created.
//
// `linkId` is the ONE deliberate exception, and it is a labelled stopgap.
// `share_link.revoke`'s payload is exactly { linkId, id } and the worker holds
// no server-side name for a link — only its id — so hiding both left the card
// with zero identifying rows: a human approving a revoke saw "Revoke this share
// link" and nothing saying WHICH. The duplicate `id` is hidden so the handle
// renders once. An opaque uuid is a poor identifier; when `share_link.revoke`
// can resolve its link's target name server-side, that NAME belongs in the
// summary and `linkId` returns to this set — the leaf name, under the same
// no-ancestor-chain bound stated above, not a path. Do NOT copy this exception to
// `permId` or any other id — summary-naming is the rule, not un-hiding.
const HIDDEN_KEYS = new Set([
  'folderId',
  'teamId',
  'tenantId',
  'targetId',
  'permId',
  'fileId',
  'targetFileId',
  'chainId',
  'id',
  'parentId',
  'appRootFileId',
  'workspaceId',
  'recipientGrants',
  'summary',
])

interface Row {
  label: string
  value: string
}

/** Build the compact key/value rows shown under the summary. */
function displayRows(meta: AccessRequestMeta): Row[] {
  const rows: Row[] = []
  const p = meta.payload
  for (const [key, raw] of Object.entries(p)) {
    if (HIDDEN_KEYS.has(key)) continue
    if (raw == null || typeof raw === 'object') continue
    let value = String(raw)
    if (key === 'permission' || key === 'level') value = permissionLabel(raw)
    if (key === 'targetType') {
      // Surface the target's KIND ("team"/"person") only if there's no
      // friendlier target label already present.
      if ('targetName' in p || 'targetLabel' in p) continue
      value = targetTypeLabel(raw)
    }
    const label = KEY_LABELS[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())
    rows.push({ label, value })
  }
  return rows
}

export function AccessApprovalCard({ request: _request, meta, onReply }: Props) {
  const [status, setStatus] = useState<'idle' | 'running' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)

  const title = TITLES[`${meta.tool}.${meta.action}`] ??
    meta.action.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
  const rows = displayRows(meta)

  const runApprove = async () => {
    // Server-executed mutations run in the WORKER after approval. The card is
    // consent only: reply Approved and let the worker perform + report it. The
    // browser must NOT issue a request for these.
    //
    // Server-executed actions have NO entry in ACCESS_ACTIONS (team.* since
    // #386, chain_folder's `create_mount` since #518A). That absence is the
    // signal — and it has to be, because an OLD bundle makes this same decision
    // and can never be taught a new flag, so a server-executed action must be
    // named something no table resolves rather than marked.
    const spec = getAccessActionSpec(meta.tool, meta.action)
    if (!spec) {
      onReply([['Approved']])
      return
    }
    setStatus('running')
    setError(null)
    const res = await performAccessAction(meta.tool, meta.action, meta.payload)
    if (res.success) {
      // Card unmounts when the reply clears the pending question.
      onReply([['Approved']], res.data ?? { ok: true })
    } else {
      // Keep the card mounted so the user sees WHAT failed; they can
      // retry or report the error to the assistant.
      setError(res.error ?? 'Request failed')
      setStatus('error')
    }
  }

  const reportError = () => {
    // Thread the failure as `_result` so the worker can relay it, then let
    // the card unmount.
    onReply([['Approved']], { error: error ?? 'Request failed' })
  }

  const decline = () => onReply([['Decline']])

  const running = status === 'running'

  return (
    <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3 my-2" data-testid="access-approval">
      <div className="flex items-start gap-2">
        <div className="shrink-0 mt-0.5">{iconFor(meta.tool)}</div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium themed-text">{title}</p>

          {/* `break-words` like the value rows and the error line below. This is
              the one text node on the card that lacked it, and it is now the
              node that carries recipient EMAILS — a single address is one
              unbreakable token, and the nearest scroll container has
              `overflow-y-auto`, so `overflow-x` computes to `auto` and one long
              address gives the WHOLE transcript a horizontal scrollbar rather
              than clipping here. */}
          {meta.summary && (
            <p className="mt-1 text-xs themed-text-secondary leading-relaxed break-words">{meta.summary}</p>
          )}

          {rows.length > 0 && (
            <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 rounded bg-black/20 px-2 py-1.5">
              {rows.map((r, i) => (
                <div key={i} className="contents">
                  <dt className="text-[10px] themed-text-muted">{r.label}</dt>
                  <dd className="text-[11px] themed-text break-words">{r.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {/* Error state — visible + actionable (retry / report). */}
          {status === 'error' && error && (
            <div className="mt-2 flex items-start gap-1.5 rounded bg-red-600/10 px-2 py-1.5">
              <AlertTriangle size={11} className="text-red-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-red-400 break-words">{error}</p>
            </div>
          )}

          <div className="flex items-center gap-2 mt-2.5">
            {status === 'error' ? (
              <>
                <button
                  onClick={runApprove}
                  className="flex items-center gap-1 px-2.5 py-1 rounded text-xs themed-btn-primary transition-opacity hover:opacity-80"
                >
                  <RefreshCw size={10} />
                  Retry
                </button>
                <button
                  onClick={reportError}
                  className="flex items-center gap-1 px-2.5 py-1 rounded text-xs themed-bg-tertiary themed-text hover:opacity-80 transition-opacity"
                >
                  <Megaphone size={10} />
                  Tell the assistant
                </button>
                <button
                  onClick={decline}
                  className="flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-red-600/20 hover:bg-red-600/30 text-red-400 transition-colors"
                >
                  <X size={10} />
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={runApprove}
                  disabled={running}
                  className="flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-green-600 hover:bg-green-500 text-white transition-colors disabled:opacity-50"
                >
                  {running ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                  {running ? 'Working…' : 'Approve'}
                </button>
                <button
                  onClick={decline}
                  disabled={running}
                  className="flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-red-600/20 hover:bg-red-600/30 text-red-400 transition-colors disabled:opacity-50"
                >
                  <X size={10} />
                  Decline
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
